import React from "react"
import { graphql } from "gatsby"

import { SEO } from "components"
import {
  Embed,
  Form,
  ImageCards,
  ProductList,
  SectionHeader,
  SideBySide,
  TalkingShopFeed,
  TextBlock,
  WhyPageCards,
} from "slices"

import HomeHero from "./_HomeHero"
import Hero from "./_Hero"

function Page({ data, pageContext }) {
  const { contentfulPage } = data

  const SetHero =
    contentfulPage.slug === "/" ? (
      <HomeHero {...contentfulPage} />
    ) : (
      <Hero {...contentfulPage} />
    )

  return (
    <>
      <SEO
        title={contentfulPage?.seoTitle}
        description={contentfulPage?.seoDescription?.internal?.content}
        image={contentfulPage?.seoImage}
      />
      <main>
        {SetHero}
        {contentfulPage?.content?.map(({ __typename, ...rest }, i) => {
          switch (__typename) {
            case "ContentfulProductList": {
              return <ProductList {...rest} key={`product-list-${i}`} />
            }
            case "ContentfulEmbed": {
              return <Embed {...rest} key={`content-embed-${i}`} />
            }
            case "ContentfulForm": {
              return <Form {...rest} key={`content-form-${i}`} />
            }
            case "ContentfulImageCards": {
              return <ImageCards {...rest} key={`content-imageCards--${i}`} />
            }
            case "ContentfulSectionHeader": {
              return (
                <SectionHeader {...rest} key={`content-sectionHeader--${i}`} />
              )
            }
            case "ContentfulSideBySide": {
              return <SideBySide {...rest} key={`content-sideBySide--${i}`} />
            }
            case "ContentfulTalkingShopFeed": {
              return (
                <TalkingShopFeed
                  {...rest}
                  feeds={pageContext.talkingShopFeeds}
                  key={`content-contentFeed--${i}`}
                />
              )
            }
            case "ContentfulTextBlock": {
              return <TextBlock {...rest} key={`content-textBlock--${i}`} />
            }
            case "ContentfulWhyPageCards": {
              return (
                <WhyPageCards {...rest} key={`content-whyPageCards--${i}`} />
              )
            }
            default:
              return null
          }
        })}
      </main>
    </>
  )
}

export const pageQuery = graphql`
  query PageQuery($slug: String) {
    contentfulPage(slug: { eq: $slug }) {
      slug
      seoTitle
      seoDescription {
        internal {
          content
        }
      }
      seoImage {
        ...AssetFragment
      }
      heroHeading
      heroContent {
        raw
      }
      heroImage {
        gatsbyImageData(width: 1024, layout: FULL_WIDTH)
        ...AssetFragment
      }
      heroVideo {
        ...AssetFragment
      }
      heroDecorators {
        ...DecoratorFragment
      }
      heroButtons {
        ...LinkFragment
      }
      content {
        __typename
        ... on ContentfulProductList {
          title
          subtitle
          showFilters
        }
        ... on ContentfulEmbed {
          policy
        }
        ... on ContentfulForm {
          content {
            raw
          }
          secondaryContent {
            raw
          }
          image {
            gatsbyImageData(width: 600, placeholder: BLURRED)
            ...AssetFragment
          }
          decorators {
            ...DecoratorFragment
          }
          form
        }
        ... on ContentfulImageCards {
          viewAllButton {
            label
            link {
              link
            }
          }
          decorators {
            ...DecoratorFragment
          }
          imageCards {
            ... on ContentfulImageCard {
              title
              link {
                link
              }
              image {
                gatsbyImageData(width: 360, placeholder: BLURRED, quality: 80)
                ...AssetFragment
              }
            }
          }
        }
        ... on ContentfulWhyPageCards {
          title
          size
          links {
            slug
            heroHeading
            heroImage {
              gatsbyImageData(width: 600)
              ...AssetFragment
            }
            heroVideo {
              ...AssetFragment
            }
          }
        }
        ... on ContentfulSectionHeader {
          header: heading {
            heading
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulSideBySide {
          heading
          image {
            ...AssetFragment
            gatsbyImageData(width: 600, placeholder: BLURRED, quality: 80)
          }
          decorators {
            ...DecoratorFragment
          }
          content {
            raw
          }
        }
        ... on ContentfulTalkingShopFeed {
          content {
            raw
          }
          type {
            slug
            name
          }
        }
      }
    }
  }
`

export default Page
